import { WidgetProps } from '@features/dashboard/types';
import { Box, Divider } from '@mui/material';
import { useMemo } from 'react';
import Widget, { Content, Header } from '../Widget';
import Settings from '../settings/Settings';
import { SettingsFormResultLayout } from '../settings/SettingsFormResultLayout';
import { ProductsSettingsForm } from '../settings/products/ProductsSettingsForm';
import { TenorsSettingsForm } from '../settings/tenors/TenorsSettingsForm';
import PricesProvider from './PricesContext';
import PricesGrid from './components/PricesGrid';
import { PriceGridControls } from './components/PriceGridControls';
import { PricesWidgetPayload } from './types';
import { useWidgetLifecycleTelemetry } from '../useWidgetLifecycleTelemetry';

type Props = WidgetProps & {
  payload?: PricesWidgetPayload;
  isFrozen?: boolean;
};

export const DEFAULT_PRICES_ROLLING_SETTINGS = { daily: 0, weekly: 0, monthly: 0, yearly: 0, quarterly: 0 };

export default function Prices({ payload, isFrozen, ...otherProps }: Props) {
  const { id, title } = otherProps;
  useWidgetLifecycleTelemetry(id, title, 'PricesWidget', payload);

  const initialValues = useMemo(
    () => ({
      selectedColumns: payload?.selectedColumns ?? [],
      selectedRows: payload?.selectedRows ?? [],
      columnsOrder: payload?.columnsOrder ?? [],
      userRollingRowSettings: payload?.userRollingRowSettings ?? DEFAULT_PRICES_ROLLING_SETTINGS,
      isOverrideRolling: payload?.isOverrideRolling ?? false,
      selectedBidAskColumns: payload?.selectedBidAskColumns ?? [],
      isShowCurrencyUnit: payload?.isShowCurrencyUnit ?? false,
      columnWidths: payload?.columnWidths ?? {},
    }),
    [payload]
  );

  return (
    <Widget {...otherProps}>
      <PricesProvider widgetId={id} initialValues={initialValues} isFrozen={isFrozen}>
        <Header title={title}>
          <PriceGridControls />
          <Settings id={`prices-settings-${id}`} widgetId={id}>
            <Box sx={{ display: 'flex', flex: 1, flexDirection: 'row', gap: 2, minHeight: 800, maxHeight: 800, overflow: 'auto' }}>
              <SettingsFormResultLayout title="Products" Form={<ProductsSettingsForm />} />
              <Divider orientation="vertical" flexItem />
              <SettingsFormResultLayout title="Tenors" Form={<TenorsSettingsForm />} />
            </Box>
          </Settings>
        </Header>
        <Content>
          <PricesGrid />
        </Content>
      </PricesProvider>
    </Widget>
  );
}
